import { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  GroupTextField,
  GroupTextFieldRow,
  Icon,
  Input,
} from "@elaniintc/goes-design-system";
import ScrollModal from "@/components/common/scroll-modal";
import { DialogTitle } from "@/components/ui/dialog";
import {
  DropdownList,
  type DropdownListOptions,
} from "@/components/ui/dropdown-list/dropdown-list";
import type { IDropdownListItem } from "@/components/ui/dropdown-list/dropdown-list-item";
import { DateInput } from "../../../form/components/DateInput";
import type {
  FieldExpirationConfig,
  ExpirationType,
  ExpirationTimeUnit,
  ValueInputType,
} from "../../types";
import { getCompatibleVariableTypes } from "../../types";
import { ConfirmationModalBase } from "@/components/common/modals";
import dayjs from "dayjs";
import type { NodeVariable, Variable } from "../../../types";
import { nodeVariableToGroups } from "../../../types";

// Dropdown options for expiration type
const expirationTypeOptions: IDropdownListItem[] = [
  {
    label: "Fecha de vencimiento",
    value: "expiration_date",
    icon: { type: "far", name: "calendar" },
  },
  {
    label: "Tiempo de caducidad",
    value: "expiration_time",
    icon: { type: "far", name: "timer" },
  },
];

// Dropdown options for time units
const timeUnitOptions: IDropdownListItem[] = [
  { label: "Horas", value: "hours" },
  { label: "Días", value: "days" },
  { label: "Semanas", value: "weeks" },
  { label: "Meses", value: "months" },
  { label: "Años", value: "years" },
];

interface ExpirationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (config: FieldExpirationConfig) => void;
  onDelete: () => void;
  fieldName: string;
  initialConfig?: FieldExpirationConfig;
  /** Type of the field value (for filtering variables) */
  fieldValueType?: ValueInputType;
  /** Available variables for variable selector */
  variables?: NodeVariable[];
}

export const ExpirationModal = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  fieldName,
  initialConfig,
  fieldValueType,
  variables = [],
}: ExpirationModalProps) => {
  // Ref to track if we're initializing from config (to prevent clearing fields on initial render)
  const isInitializing = useRef(false);

  // State for expiration type
  const [expirationType, setExpirationType] = useState<ExpirationType>(
    initialConfig?.type ?? "expiration_date",
  );

  // Search state for expiration type dropdown
  const [expirationTypeSearch, setExpirationTypeSearch] = useState("");

  // State for expiration date mode
  const [expirationDate, setExpirationDate] = useState<string | Variable>(
    initialConfig?.expirationDate ?? "",
  );
  const [expirationDateInputType, setExpirationDateInputType] = useState<
    "inp" | "var"
  >(initialConfig?.expirationDateInputType ?? "inp");
  const [notificationDate, setNotificationDate] = useState<string | Variable>(
    initialConfig?.notificationDate ?? "",
  );
  const [notificationDateInputType, setNotificationDateInputType] = useState<
    "inp" | "var"
  >(initialConfig?.notificationDateInputType ?? "inp");

  // State for expiration time mode
  const [expirationAmount, setExpirationAmount] = useState<string>(
    initialConfig?.expirationAmount?.toString() ?? "",
  );
  const [expirationUnit, setExpirationUnit] = useState<
    IDropdownListItem | undefined
  >(
    initialConfig?.expirationUnit
      ? timeUnitOptions.find((o) => o.value === initialConfig.expirationUnit)
      : undefined,
  );
  const [notificationAmount, setNotificationAmount] = useState<string>(
    initialConfig?.notificationAmount?.toString() ?? "",
  );
  const [notificationUnit, setNotificationUnit] = useState<
    IDropdownListItem | undefined
  >(
    initialConfig?.notificationUnit
      ? timeUnitOptions.find((o) => o.value === initialConfig.notificationUnit)
      : undefined,
  );

  // State for replacement value
  const [replacementValue, setReplacementValue] = useState<string | Variable>(
    initialConfig?.replacementValue ?? "",
  );
  const [replacementInputType, setReplacementInputType] = useState<
    "inp" | "var"
  >(initialConfig?.replacementInputType ?? "inp");

  // Search state for replacement value variable selector
  const [replacementVariableSearch, setReplacementVariableSearch] =
    useState("");

  // Search states for date variable selectors
  const [expirationDateVariableSearch, setExpirationDateVariableSearch] =
    useState("");
  const [notificationDateVariableSearch, setNotificationDateVariableSearch] =
    useState("");

  // Filter variables by field type compatibility
  const filteredVariables = useMemo(() => {
    if (!fieldValueType) return variables;

    const compatibleTypes = getCompatibleVariableTypes(fieldValueType);
    if (compatibleTypes.length === 0) return variables; // No filter - show all

    return variables
      .map((nodeVar) => ({
        ...nodeVar,
        variables: nodeVar.variables.filter((v) =>
          compatibleTypes.includes(v.type.toLowerCase()),
        ),
      }))
      .filter((nodeVar) => nodeVar.variables.length > 0);
  }, [variables, fieldValueType]);

  // Filter variables by date type for date fields
  const dateFilteredVariables = useMemo(() => {
    const dateTypes = ["date", "datetime", "date-time"];
    return variables
      .map((nodeVar) => ({
        ...nodeVar,
        variables: nodeVar.variables.filter((v) =>
          dateTypes.includes(v.type.toLowerCase()),
        ),
      }))
      .filter((nodeVar) => nodeVar.variables.length > 0);
  }, [variables]);

  // Convert inputVariables to DropdownList format
  const variableGroups = useMemo(
    () => nodeVariableToGroups(filteredVariables),
    [filteredVariables],
  );

  // Convert date filtered variables to DropdownList format
  const dateVariableGroups = useMemo(
    () => nodeVariableToGroups(dateFilteredVariables),
    [dateFilteredVariables],
  );

  // Get selected variable for expiration date
  const selectedExpirationDateVariable = useMemo(() => {
    if (expirationDateInputType === "var") {
      if (typeof expirationDate === "object" && expirationDate !== null) {
        return expirationDate as Variable;
      }
      if (typeof expirationDate === "string" && expirationDate) {
        const allVariables = (variables || []).flatMap((node) =>
          (node.variables || []).map((v) => ({
            ...v,
            node_id: node.nodeId,
            ref_name: node.ref_name,
          })),
        );
        return allVariables.find(
          (v) => v.name === expirationDate || v.id === expirationDate,
        );
      }
    }
    return null;
  }, [expirationDateInputType, expirationDate, variables]);

  // Get selected variable for notification date
  const selectedNotificationDateVariable = useMemo(() => {
    if (notificationDateInputType === "var") {
      if (typeof notificationDate === "object" && notificationDate !== null) {
        return notificationDate as Variable;
      }
      if (typeof notificationDate === "string" && notificationDate) {
        const allVariables = (variables || []).flatMap((node) =>
          (node.variables || []).map((v) => ({
            ...v,
            node_id: node.nodeId,
            ref_name: node.ref_name,
          })),
        );
        return allVariables.find(
          (v) => v.name === notificationDate || v.id === notificationDate,
        );
      }
    }
    return null;
  }, [notificationDateInputType, notificationDate, variables]);

  // Get selected dropdown item for expiration date
  const selectedExpirationDateItem = useMemo(() => {
    if (!selectedExpirationDateVariable) return undefined;
    return dateVariableGroups
      .flatMap((g) => g.items)
      .find((item) => item.value === selectedExpirationDateVariable.id);
  }, [dateVariableGroups, selectedExpirationDateVariable]);

  // Get selected dropdown item for notification date
  const selectedNotificationDateItem = useMemo(() => {
    if (!selectedNotificationDateVariable) return undefined;
    return dateVariableGroups
      .flatMap((g) => g.items)
      .find((item) => item.value === selectedNotificationDateVariable.id);
  }, [dateVariableGroups, selectedNotificationDateVariable]);

  // Get selected variable for display
  // When deserializing, replacementValue might be a string (variable name) when type is "var"
  // We need to find the matching Variable object from the variables list
  const selectedReplacementVariable = useMemo(() => {
    if (replacementInputType === "var") {
      if (typeof replacementValue === "object" && replacementValue !== null) {
        return replacementValue as Variable;
      }
      // Try to find variable by name if replacementValue is a string (from deserialization)
      if (typeof replacementValue === "string" && replacementValue) {
        // Use original variables list (not filtered) to find the variable
        const allVariables = (variables || []).flatMap((node) =>
          (node.variables || []).map((v) => ({
            ...v,
            node_id: node.nodeId,
            ref_name: node.ref_name,
          })),
        );
        const foundVariable = allVariables.find(
          (v) => v.name === replacementValue,
        );
        if (foundVariable) {
          return foundVariable;
        }
      }
    }
    return null;
  }, [replacementInputType, replacementValue, variables]);

  // Get selected dropdown item for display with icon
  const selectedReplacementItem = useMemo(() => {
    if (!selectedReplacementVariable) return undefined;

    return variableGroups
      .flatMap((g) => g.items)
      .find((item) => item.value === selectedReplacementVariable.id);
  }, [variableGroups, selectedReplacementVariable]);

  // State for delete confirmation modal
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Get selected expiration type option for display
  const selectedExpirationTypeOption = useMemo(
    () => expirationTypeOptions.find((o) => o.value === expirationType),
    [expirationType],
  );

  // Reset form when modal opens with new config
  useEffect(() => {
    if (isOpen) {
      isInitializing.current = true;
      setExpirationType(initialConfig?.type ?? "expiration_date");
      setExpirationDate(initialConfig?.expirationDate ?? "");
      setExpirationDateInputType(
        initialConfig?.expirationDateInputType ?? "inp",
      );
      setNotificationDate(initialConfig?.notificationDate ?? "");
      setNotificationDateInputType(
        initialConfig?.notificationDateInputType ?? "inp",
      );
      setExpirationAmount(initialConfig?.expirationAmount?.toString() ?? "");
      setExpirationUnit(
        initialConfig?.expirationUnit
          ? timeUnitOptions.find(
              (o) => o.value === initialConfig.expirationUnit,
            )
          : undefined,
      );
      setNotificationAmount(
        initialConfig?.notificationAmount?.toString() ?? "",
      );
      setNotificationUnit(
        initialConfig?.notificationUnit
          ? timeUnitOptions.find(
              (o) => o.value === initialConfig.notificationUnit,
            )
          : undefined,
      );
      setReplacementValue(initialConfig?.replacementValue ?? "");
      setReplacementInputType(initialConfig?.replacementInputType ?? "inp");

      // After a short delay, mark initialization as complete
      setTimeout(() => {
        isInitializing.current = false;
      }, 0);
    }
  }, [isOpen, initialConfig]);

  // Clear fields when expiration type changes (but not during initialization)
  useEffect(() => {
    // Don't clear fields if we're initializing from config
    if (isInitializing.current) return;

    if (expirationType === "expiration_date") {
      // Clear time-based fields when switching to date mode
      setExpirationAmount("");
      setExpirationUnit(undefined);
      setNotificationAmount("");
      setNotificationUnit(undefined);
    } else {
      // Clear date-based fields when switching to time mode
      setExpirationDate("");
      setExpirationDateInputType("inp");
      setNotificationDate("");
      setNotificationDateInputType("inp");
      // Set default unit to "days" when switching to time mode
      if (!expirationUnit) {
        setExpirationUnit(timeUnitOptions.find((o) => o.value === "days"));
      }
    }
  }, [expirationType]);

  // Validation
  const isValid = useMemo(() => {
    // Check if replacement value is valid
    // For var mode: either have the Variable object directly, or found it via selectedReplacementVariable
    const hasReplacementValue =
      replacementInputType === "var"
        ? (typeof replacementValue === "object" && !!replacementValue) ||
          !!selectedReplacementVariable
        : typeof replacementValue === "string" && !!replacementValue.trim();

    if (expirationType === "expiration_date") {
      // Check expiration date based on input type
      const hasExpirationDate =
        expirationDateInputType === "var"
          ? (typeof expirationDate === "object" && !!expirationDate) ||
            !!selectedExpirationDateVariable
          : typeof expirationDate === "string" && !!expirationDate.trim();

      return hasExpirationDate && hasReplacementValue;
    } else {
      // For expiration_time: expirationAmount and expirationUnit are required
      // notificationAmount and notificationUnit are both optional, but if one is set, both must be set
      const hasExpirationData = !!expirationAmount && !!expirationUnit;
      const hasValidNotification =
        (!notificationAmount.trim() && !notificationUnit) || // both empty is valid
        (!!notificationAmount.trim() && !!notificationUnit); // both filled is valid

      return hasExpirationData && hasValidNotification && hasReplacementValue;
    }
  }, [
    expirationType,
    expirationDate,
    expirationDateInputType,
    selectedExpirationDateVariable,
    expirationAmount,
    expirationUnit,
    notificationAmount,
    notificationUnit,
    replacementValue,
    replacementInputType,
    selectedReplacementVariable,
  ]);

  const handleSave = () => {
    // Use the resolved variable if we found it from a string name
    const finalReplacementValue =
      replacementInputType === "var" &&
      selectedReplacementVariable &&
      typeof replacementValue === "string"
        ? selectedReplacementVariable
        : replacementValue;

    // Resolve expiration date variable
    const finalExpirationDate =
      expirationDateInputType === "var" &&
      selectedExpirationDateVariable &&
      typeof expirationDate === "string"
        ? selectedExpirationDateVariable
        : expirationDate;

    // Resolve notification date variable
    const finalNotificationDate =
      notificationDateInputType === "var" &&
      selectedNotificationDateVariable &&
      typeof notificationDate === "string"
        ? selectedNotificationDateVariable
        : notificationDate;

    const config: FieldExpirationConfig = {
      type: expirationType,
      replacementValue: finalReplacementValue,
      replacementInputType,
    };

    if (expirationType === "expiration_date") {
      config.expirationDate = finalExpirationDate;
      config.expirationDateInputType = expirationDateInputType;
      if (notificationDate || notificationDateInputType === "var") {
        config.notificationDate = finalNotificationDate;
        config.notificationDateInputType = notificationDateInputType;
      }
    } else {
      config.expirationAmount = Number(expirationAmount);
      config.expirationUnit = expirationUnit?.value as ExpirationTimeUnit;
      if (notificationAmount && notificationUnit) {
        config.notificationAmount = Number(notificationAmount);
        config.notificationUnit = notificationUnit.value as ExpirationTimeUnit;
      }
    }

    onSave(config);
    onClose();
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    onDelete();
    setShowDeleteConfirm(false);
    onClose();
  };

  const handleNumberInput = (value: string): string => {
    // Only allow positive integers
    return value.replace(/[^0-9]/g, "");
  };

  const handleReplacementInputTypeChange = () => {
    // Toggle between input and variable modes, clear value when switching
    setReplacementInputType((prev) => {
      const newType = prev === "inp" ? "var" : "inp";
      // Clear value when switching modes
      setReplacementValue("");
      return newType;
    });
  };

  return (
    <>
      <ScrollModal
        isOpen={isOpen}
        onClose={onClose}
        modalContentClassName="w-full max-w-[520px] max-h-[680px] h-auto flex flex-col gap-0"
        header={
          <div className="flex items-center justify-between px-6 py-4">
            <DialogTitle className="text-h3 font-semibold text-heading">
              Caducidad del valor {fieldName ? `- ${fieldName}` : ""}
            </DialogTitle>
            <Button
              type="button"
              onClick={onClose}
              variant="quiet"
              aria-label="Cerrar"
              className="w-8 h-8 cursor-pointer"
            >
              <Icon type="far" name="times" />
            </Button>
          </div>
        }
        footer={
          <div className="flex justify-between gap-2 py-4 px-6">
            {initialConfig ? (
              <Button
                type="button"
                variant="danger-tertiary"
                className="font-semibold"
                onClick={handleDelete}
              >
                Eliminar
              </Button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <Button
                type="button"
                variant="tertiary"
                className="font-semibold"
                onClick={onClose}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                className="font-semibold"
                disabled={!isValid}
                onClick={handleSave}
              >
                Guardar cambios
              </Button>
            </div>
          </div>
        }
      >
        <div className="p-6 flex flex-col gap-4">
          {/* Expiration Type Selector */}
          <GroupTextField label="Tipo de caducidad" required>
            <DropdownList
              options={expirationTypeOptions}
              selectedValue={selectedExpirationTypeOption}
              toggleSelection={(item: IDropdownListItem) => {
                setExpirationType(item.value as ExpirationType);
              }}
              placeholder="Seleccionar tipo"
              search={{
                placeholder: "Buscar tipo...",
                query: expirationTypeSearch,
                onSearch: setExpirationTypeSearch,
              }}
              widthClassName="w-full"
              heightClassName="max-h-40"
            />
          </GroupTextField>

          {/* Expiration Date Mode */}
          {expirationType === "expiration_date" && (
            <>
              <GroupTextField label="Fecha de caducidad" required>
                <GroupTextFieldRow>
                  <Button
                    variant="quiet"
                    type="button"
                    group="left"
                    className="font-mono min-w-12"
                    onClick={() => {
                      setExpirationDateInputType((prev) => {
                        const newType = prev === "inp" ? "var" : "inp";
                        setExpirationDate("");
                        return newType;
                      });
                    }}
                  >
                    {expirationDateInputType}
                  </Button>
                  {expirationDateInputType === "inp" ? (
                    <DateInput
                      value={
                        typeof expirationDate === "string" ? expirationDate : ""
                      }
                      onChangeDate={(val) => {
                        if (val) {
                          setExpirationDate(dayjs(val).format("DD/MM/YYYY"));
                        } else {
                          setExpirationDate("");
                        }
                      }}
                      placeholder="DD/MM/YYYY"
                      group="right"
                    />
                  ) : (
                    <DropdownList
                      options={dateVariableGroups}
                      selectedValue={selectedExpirationDateItem}
                      toggleSelection={(item: IDropdownListItem) => {
                        const variable = dateFilteredVariables
                          .flatMap((nv) => nv.variables)
                          .find((v) => v.id === item.value);
                        if (variable) {
                          setExpirationDate(variable);
                        }
                      }}
                      placeholder="Selecciona una variable"
                      search={{
                        placeholder: "Buscar variable...",
                        query: expirationDateVariableSearch,
                        onSearch: setExpirationDateVariableSearch,
                      }}
                      group="right"
                      widthClassName="flex-1"
                    />
                  )}
                </GroupTextFieldRow>
              </GroupTextField>

              <GroupTextField label="Notificación de caducidad">
                <GroupTextFieldRow>
                  <Button
                    variant="quiet"
                    type="button"
                    group="left"
                    className="font-mono min-w-12"
                    onClick={() => {
                      setNotificationDateInputType((prev) => {
                        const newType = prev === "inp" ? "var" : "inp";
                        setNotificationDate("");
                        return newType;
                      });
                    }}
                  >
                    {notificationDateInputType}
                  </Button>
                  {notificationDateInputType === "inp" ? (
                    <DateInput
                      value={
                        typeof notificationDate === "string"
                          ? notificationDate
                          : ""
                      }
                      onChangeDate={(val) => {
                        if (val) {
                          setNotificationDate(dayjs(val).format("DD/MM/YYYY"));
                        } else {
                          setNotificationDate("");
                        }
                      }}
                      placeholder="DD/MM/YYYY"
                      group="right"
                    />
                  ) : (
                    <DropdownList
                      options={dateVariableGroups}
                      selectedValue={selectedNotificationDateItem}
                      toggleSelection={(item: IDropdownListItem) => {
                        const variable = dateFilteredVariables
                          .flatMap((nv) => nv.variables)
                          .find((v) => v.id === item.value);
                        if (variable) {
                          setNotificationDate(variable);
                        }
                      }}
                      placeholder="Selecciona una variable"
                      search={{
                        placeholder: "Buscar variable...",
                        query: notificationDateVariableSearch,
                        onSearch: setNotificationDateVariableSearch,
                      }}
                      group="right"
                      widthClassName="flex-1"
                    />
                  )}
                </GroupTextFieldRow>
              </GroupTextField>
            </>
          )}

          {/* Expiration Time Mode */}
          {expirationType === "expiration_time" && (
            <>
              <GroupTextField label="Caducar al transcurrir" required>
                <GroupTextFieldRow>
                  <Input
                    group="left"
                    className="flex-1"
                    placeholder="Escribe un número"
                    value={expirationAmount}
                    onChange={(e) =>
                      setExpirationAmount(handleNumberInput(e.target.value))
                    }
                  />
                  <DropdownList
                    options={timeUnitOptions as DropdownListOptions}
                    selectedValue={expirationUnit}
                    placeholder="Días"
                    toggleSelection={setExpirationUnit}
                    widthClassName="w-28"
                    group="right"
                  />
                </GroupTextFieldRow>
              </GroupTextField>

              <GroupTextField label="Enviar notificación de caducidad al transcurrir">
                <GroupTextFieldRow>
                  <Input
                    group="left"
                    className="flex-1"
                    placeholder="Escribe un número"
                    value={notificationAmount}
                    onChange={(e) =>
                      setNotificationAmount(handleNumberInput(e.target.value))
                    }
                  />
                  <DropdownList
                    options={timeUnitOptions as DropdownListOptions}
                    selectedValue={notificationUnit}
                    placeholder="Años"
                    toggleSelection={setNotificationUnit}
                    widthClassName="w-28"
                    group="right"
                  />
                </GroupTextFieldRow>
              </GroupTextField>
            </>
          )}

          {/* Replacement Value */}
          <div className="pt-2">
            <GroupTextField label="Valor de reemplazo" required>
              <GroupTextFieldRow>
                <Button
                  variant="quiet"
                  type="button"
                  group="left"
                  className="font-mono min-w-12"
                  onClick={handleReplacementInputTypeChange}
                >
                  {replacementInputType}
                </Button>
                {replacementInputType === "inp" ? (
                  <Input
                    group="right"
                    className="w-full"
                    placeholder="Texto de reemplazo"
                    value={
                      typeof replacementValue === "string"
                        ? replacementValue
                        : typeof replacementValue === "object" &&
                            replacementValue !== null
                          ? "" // Clear if it's a Variable object in inp mode
                          : ""
                    }
                    onChange={(e) => setReplacementValue(e.target.value)}
                  />
                ) : (
                  <DropdownList
                    options={variableGroups}
                    selectedValue={selectedReplacementItem}
                    toggleSelection={(item: IDropdownListItem) => {
                      // Use filteredVariables to find the selected variable
                      const variable = filteredVariables
                        .flatMap((nv) => nv.variables)
                        .find((v) => v.id === item.value);
                      if (variable) {
                        setReplacementValue(variable);
                      }
                    }}
                    placeholder="Selecciona una variable"
                    search={{
                      placeholder: "Buscar variable...",
                      query: replacementVariableSearch,
                      onSearch: setReplacementVariableSearch,
                    }}
                    group="right"
                    widthClassName="flex-1"
                  />
                )}
              </GroupTextFieldRow>
            </GroupTextField>
            <p className="text-xs text-body-quiet mt-1">
              Este será el valor asignado una vez el tiempo llegue a su fin.
            </p>
          </div>
        </div>
      </ScrollModal>

      {/* Delete Confirmation Modal */}
      <ConfirmationModalBase
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onAccept={confirmDelete}
        title="Eliminar caducidad"
        description="¿Estás seguro de que deseas eliminar la caducidad del valor? El valor configurado en el campo no tendrá vencimiento."
        cancelText="Cancelar"
        acceptText="Eliminar caducidad"
        acceptVariant="danger-secondary"
        isLoading={false}
      />
    </>
  );
};

export default ExpirationModal;
